// Piggington's Daily Question — one question a day per child on Kid Home, paying Oinks only
// (never tokens, so it needs no cap logic, no cooldowns, and no parent admin beyond the
// existing Oinks toggle). See lib/oinks.js for the Law-1 rules everything here inherits.
//
// Everything is DETERMINISTIC per child per day, seeded from `dateStr + childId`: the same
// question shows on every device all day, nothing is written to state until the child
// answers, and a Playwright spec can recompute the expected question by importing this
// module directly (see tests/specs/13-daily-question.spec.js). The one soft edge: a vocab-day
// question re-rolls to maths if the eligible vocab pool changes mid-day (e.g. a parent adds
// the first vocab words at lunchtime) — rare and harmless, since nothing was answered yet.

import { DEFAULT_TT_TABLES, resolveMathsBand } from "../lib/maths.js";
import { makeVocabContentRefresher, resolveVocabCategory } from "../lib/vocab.js";
import { weekDatesFrom, weekStartOf } from "../lib/utils.js";

/* ------------------------------ seeded randomness ------------------------------ */

export function hashStr(s) {
  let hash = 0;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(hash);
}

// mulberry32 — tiny, well-distributed PRNG. Each daily question draws all of its randomness
// from one seeded instance, so a given (child, day) always yields the identical question.
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function rngPick(rng, list) { return list[Math.floor(rng() * list.length)]; }
function rngInt(rng, min, max) { return min + Math.floor(rng() * (max - min + 1)); }

// Deterministic Fisher-Yates (the lib/constants.js shuffle uses Math.random, which would
// break the "same question all day" guarantee for vocab option ordering).
function rngShuffle(rng, list) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ------------------------------ rewards ------------------------------ */

export const DAILY_OP_CORRECT = 3;
export const DAILY_OP_WRONG = 1; // paid for showing up — the daily habit is the behaviour being trained

// Roughly 1 day in 7 per child, seed-derived rather than random so it can never be re-rolled
// by reloading, and different children hit their double days on different dates.
export function isDoubleOinkDay(childId, dateStr) {
  return hashStr(`${dateStr}|${childId}|double`) % 7 === 0;
}

// LB-248 — Double token day (Alan, 9 Sep 2026): once a calendar month per child, everything a
// child earns through their own effort — earning activities and all four Learn & Earn
// games — pays double. NOT the weekly streak reward, NOT parent adjustments, and the weekly
// spending cap still stands (a double day reaches it sooner; over the cap → a request, as
// ever). Same seed-hash idea as the Oink day: the day is derived from the month + childId,
// so it cannot be re-rolled, siblings get different days, and every device agrees. Never a
// Monday (Money Monday is already the loud day).
export function doubleTokenDayOf(childId, yearMonth) {
  const [y, m] = yearMonth.split("-").map(Number);
  const days = new Date(y, m, 0).getDate();
  let day = 1 + (hashStr(`${yearMonth}|${childId}|double-token`) % days);
  const dow = new Date(y, m - 1, day).getDay(); // 0 Sun … 1 Mon
  if (dow === 1) day = day === days ? 1 : day + 1;
  return `${yearMonth}-${String(day).padStart(2, "0")}`;
}
export function isDoubleTokenDay(childId, dateStr, settings) {
  if (settings && settings.doubleTokenDay === false) return false;
  if (!childId || !dateStr) return false;
  // Preview-only hook (Root.jsx AppPreview): when a force date is set it IS the double day, so
  // every other date is ordinary. It used to only force true on a match and otherwise fall
  // through to the hash — so on the day the hash happened to land (Alex's was 17 Sep 2026)
  // spec 52's "ordinary day" case saw the real banner and failed until midnight (LB-321).
  if (settings && typeof settings.doubleTokenDayForce === "string" && settings.doubleTokenDayForce) return settings.doubleTokenDayForce === dateStr;
  return doubleTokenDayOf(childId, dateStr.slice(0, 7)) === dateStr;
}

export function dailyQuestionOP(result, double) {
  const base = result === "correct" ? DAILY_OP_CORRECT : DAILY_OP_WRONG;
  return double ? base * 2 : base;
}

// Perfect Week bonus (Aug 2026 characters/economy task, oinks-pricing-strategy.md §7.3):
// answered the Daily Question on all 7 days of the CURRENT week — correctness not required,
// same "paid for showing up" philosophy as DAILY_OP_WRONG above; consistency is the trained
// habit here, not accuracy. Awarded once, on the 7th day's answer. One free session's worth
// (a typical Learn & Earn session ≈ 15 OP). App.jsx's onAnswerDailyQuestion is the one write
// path — it reuses the app's existing week boundary (weekStartOf/weekDatesFrom, lib/utils.js
// — the same one the L&E weekly token caps use) rather than any new week logic here.
export const PERFECT_WEEK_OP = 15;

// Pure core of the Perfect Week check — Node-safe (no React), so it's directly unit-testable
// the same way vaultInterest.js's maths is (see tests/specs/25-characters-economy.spec.js).
// App.jsx's onAnswerDailyQuestion calls this once per answer and writes the result into
// learningContent[childId].stats.dailyQWeek + .dailyQ.perfectWeek; nothing here touches state
// itself. `dailyQWeek` is the child's existing { week, dates } tracker (or null/undefined for
// a fresh child) — `dates` accumulates each distinct date answered while `week` stays the
// same, and resets the moment a new week's date is passed in (no explicit "clear" needed).
// Returns the updated tracker plus whether TODAY's answer just completed a perfect week —
// which, given one-answer-per-day is already enforced by App.jsx's own double-submit guard,
// can only become true once per week: the day after, `week` has moved on and `dates` resets.
export function recordDailyQuestionForPerfectWeek(dailyQWeek, today) {
  const week = weekStartOf(today);
  const priorDates = (dailyQWeek && dailyQWeek.week === week && Array.isArray(dailyQWeek.dates)) ? dailyQWeek.dates : [];
  const dates = priorDates.includes(today) ? priorDates : [...priorDates, today];
  const perfectWeek = weekDatesFrom(week).every((d) => dates.includes(d));
  return { dailyQWeek: { week, dates }, perfectWeek };
}

/* ------------------------------ question generation ------------------------------ */

// Self-contained deterministic arithmetic per band — deliberately NOT reusing maths.js's
// GENERATORS (they draw from Math.random internally and can't be seeded without refactoring
// the games). Two shapes per band keeps the daily ritual varied without a content burden.
function mathsQuestion(rng, bandKey) {
  switch (bandKey) {
    case "foundations": {
      if (rng() < 0.5) { const a = rngInt(rng, 2, 10), b = rngInt(rng, 2, 10); return { prompt: `${a} + ${b}`, answer: a + b }; }
      const a = rngInt(rng, 5, 15), b = rngInt(rng, 1, 5); return { prompt: `${a} − ${b}`, answer: a - b };
    }
    case "strategist": {
      if (rng() < 0.5) { const a = rngInt(rng, 3, 9), b = rngInt(rng, 3, 9), c = rngInt(rng, 2, 6); return { prompt: `(${a} + ${b}) × ${c}`, answer: (a + b) * c }; }
      const n = rngInt(rng, 4, 12); return { prompt: `What is ${n}²?`, answer: n * n };
    }
    case "genius": {
      if (rng() < 0.5) { const pct = rngPick(rng, [10, 20, 25, 50]); const base = rngPick(rng, [40, 60, 80, 120, 200]); return { prompt: `What is ${pct}% of ${base}?`, answer: (base * pct) / 100 }; }
      const root = rngInt(rng, 5, 15); return { prompt: `What is the square root of ${root * root}?`, answer: root };
    }
    case "builder":
    default: {
      if (rng() < 0.5) { const a = rngInt(rng, 3, 12), b = rngInt(rng, 3, 12); return { prompt: `${a} × ${b}`, answer: a * b }; }
      const a = rngInt(rng, 12, 60), b = rngInt(rng, 11, 40); return { prompt: `${a} + ${b}`, answer: a + b };
    }
  }
}

// The vocab pool for the daily question: the child's ACTIVE list, still in rotation, but
// deliberately INCLUDING mastered words (correctCount >= 2) — the daily question is where
// retired words quietly resurface for a refresher, a free first slice of spaced repetition
// ahead of R3 proper. Only words with a full 4-option set qualify (MCQ needs distractors).
function eligibleDailyVocab(child, learning) {
  const bank = learning.vocabBank || [];
  const activeSource = resolveVocabCategory(child, learning.vocabCategory);
  return bank.filter((v) => {
    const inActiveList = activeSource === "custom" ? (!v.source || v.source === "custom") : v.source === activeSource;
    return inActiveList && v.testInclude !== false && Array.isArray(v.options) && v.options.length >= 4;
  });
}

// The day's question for this child: { kind: "tt" | "maths" | "vocab", prompt, answer } for
// numeric kinds, or { kind: "vocab", word, definition, options } for an MCQ day.
// Adaptive (Alan, 10 Sep): when the household has Adaptive and the child has a vocab state,
// the vocab pool is the adaptive draw at the child's target — an achievable question — and
// results still never count (adaptiveRecord ignores kind "daily"). The day's subject rotates
// as before; `adaptiveVocabPool` is the caller's pre-drawn pool (ChildView), or null.
export function dailyQuestionFor(child, learning, dateStr, adaptiveVocabPool = null) {
  const rng = mulberry32(hashStr(`${dateStr}|${child.id}|q`));
  const vocabPool = adaptiveVocabPool && adaptiveVocabPool.length ? adaptiveVocabPool : eligibleDailyVocab(child, learning || {});
  const kinds = vocabPool.length > 0 ? ["tt", "maths", "vocab"] : ["tt", "maths"];
  const kind = rngPick(rng, kinds);
  if (kind === "vocab") {
    // Same serve-time CMS refresh as the vocab test's pool in ChildView — the bank's baked
    // definition/options can be stale relative to the published list (see
    // makeVocabContentRefresher in lib/vocab.js). Refresh AFTER the pick, not before: the
    // pick itself must stay a pure function of (date, child, stored bank) so the day's
    // question doesn't change identity when the bundle finishes loading mid-day.
    const item = makeVocabContentRefresher()(rngPick(rng, vocabPool));
    return { kind, word: item.word, definition: item.definition, options: rngShuffle(rng, item.options.slice(0, 4)) };
  }
  if (kind === "tt") {
    const tables = learning && learning.ttTables && learning.ttTables.length > 0 ? learning.ttTables : DEFAULT_TT_TABLES;
    const table = rngPick(rng, tables);
    const mult = rngInt(rng, 2, 12);
    const flip = rng() < 0.5;
    return { kind, prompt: `${flip ? table : mult} × ${flip ? mult : table}`, answer: table * mult };
  }
  return { kind: "maths", ...mathsQuestion(rng, resolveMathsBand(child, learning && learning.mathsBand)) };
}
